import { coversCourses, coversLiving } from '../../../../../utilities/sponsors';
import { OrgType } from 'api-paths-cas';

type Fields = {
  orgType: string;
  sponsorName: string;
  isMultiCountry?: boolean;
  sponsorshipCovers: Array<string>;
  courseFundType: string;
  courseFundPercentage: number;
  courseFundAmount: number;
  livingFundType: string;
  livingFundPercentage: number;
  livingFundAmountType: string;
  livingFundAmount: number;
  courseAndLivingFundSeparate?: boolean;
  courseAndLivingFundType: string;
  courseAndLivingFundPercentage: number;
  courseAndLivingFundAmount: number;
};

type Visible = Record<
  'orgType' | 'isMultiCountry' | 'sponsorName' | 'fundingCover' | 'courseForm' | 'livingForm' | 'courseAndLivingForm',
  boolean
>;

export const internationalOrgs: string[] = [OrgType.INT_ORG, OrgType.INT_COMP];

export const getOrgSponsorType = (orgType: OrgType): string => {
  if (internationalOrgs.includes(orgType)) {
    return 'companySponsorship';
  }
  if ([OrgType.BRITISH_GOV, OrgType.UK_GOV, OrgType.YOUR_GOV].includes(orgType)) {
    return 'governmentSponsorship';
  }
  return '';
};

function courseFundComplete(fields: Fields) {
  const { courseFundType, courseFundPercentage, courseFundAmount } = fields;

  if (courseFundType === '') return false;
  if (courseFundType === 'Percentage' && courseFundPercentage === 0) return false;
  if (courseFundType === 'Amount' && courseFundAmount === 0) return false;

  return true;
}

function livingFundComplete(fields: Fields) {
  const { livingFundType, livingFundPercentage, livingFundAmountType, livingFundAmount } = fields;

  if (livingFundType === '') return false;
  if (livingFundType === 'Percentage' && livingFundPercentage === 0) return false;
  if (livingFundType === 'Amount') {
    if (livingFundAmountType === '') return false;
    if (livingFundAmount === 0) return false;
  }

  return true;
}

function courseAndLivingFundComplete(fields: Fields) {
  const { courseAndLivingFundType, courseAndLivingFundPercentage, courseAndLivingFundAmount } = fields;

  if (courseAndLivingFundType === '') return false;
  if (courseAndLivingFundType === 'Percentage' && courseAndLivingFundPercentage === 0) return false;
  if (courseAndLivingFundType === 'Amount' && courseAndLivingFundAmount === 0) return false;

  return true;
}

export function getPageStatus(fields: Fields): {
  complete: boolean;
  visible: Visible;
} {
  const { orgType, sponsorName, isMultiCountry, sponsorshipCovers, courseAndLivingFundSeparate } = fields;

  const complete: boolean = (() => {
    if (orgType === '') return false;
    if (internationalOrgs.includes(orgType) && isMultiCountry === undefined) return false;
    if (sponsorName === '') return false;

    if (coversCourses(sponsorshipCovers) && coversLiving(sponsorshipCovers)) {
      if (courseAndLivingFundSeparate === true) {
        if (courseFundComplete(fields) === false) return false;
        if (livingFundComplete(fields) === false) return false;
      } else if (courseAndLivingFundSeparate === false) {
        if (courseAndLivingFundComplete(fields) === false) return false;
      } else {
        return false;
      }
    } else if (coversCourses(sponsorshipCovers)) {
      if (courseFundComplete(fields) === false) return false;
    } else if (coversLiving(sponsorshipCovers)) {
      if (livingFundComplete(fields) === false) return false;
    } else {
      return false;
    }

    return true;
  })();

  const visible: Visible = {
    orgType: true,
    isMultiCountry: internationalOrgs.includes(orgType),
    sponsorName: true,
    fundingCover: true,
    courseForm: coversCourses(sponsorshipCovers) && !coversLiving(sponsorshipCovers),
    livingForm: !coversCourses(sponsorshipCovers) && coversLiving(sponsorshipCovers),
    courseAndLivingForm: coversCourses(sponsorshipCovers) && coversLiving(sponsorshipCovers),
  };

  return {
    complete,
    visible,
  };
}
